import { Logo } from "@/components/site/Logo";
import { DISCLAIMER_AGCM, COMPANY } from "@/lib/content";
import { Mail, Phone, MapPin, ShieldCheck, BadgeCheck } from "lucide-react";

const MontoroBlock = ({ heading }) => (
  <div>
    <p className="text-xs font-bold uppercase tracking-widest text-gold">{heading}</p>
    <p className="mt-2 font-heading text-base font-extrabold text-white">{COMPANY.legalName}</p>
    <p className="text-sm text-white/60">{COMPANY.marchio}</p>
    <ul className="mt-3 space-y-1.5 text-sm text-white/60">
      <li className="flex items-start gap-2.5">
        <MapPin className="mt-0.5 h-4 w-4 flex-none text-gold" /> {COMPANY.address}
      </li>
      <li>P.IVA / C.F. {COMPANY.piva}</li>
      <li>REA: {COMPANY.rea}</li>
      <li>PEC: {COMPANY.pec}</li>
      <li>
        <a href={COMPANY.emailHref} className="transition-colors hover:text-gold">Email: {COMPANY.email}</a>
      </li>
      <li>Privacy: {COMPANY.privacyEmail}</li>
      <li>
        <a href={COMPANY.phoneHref} className="transition-colors hover:text-gold">Tel: {COMPANY.phone}</a>
      </li>
      <li>Capitale sociale: {COMPANY.capitale}</li>
    </ul>
  </div>
);

export const Footer = ({ partner }) => {
  const year = new Date().getFullYear();
  const isSegnalatore = partner && (partner.tipo_partner || "").toLowerCase().includes("segnalatore");
  const partnerLabel = isSegnalatore ? "Segnalatore ufficiale" : "Partner ufficiale";
  const titolar = partner ? partner.company_name : COMPANY.legalName;

  return (
    <footer id="contatti" className="bg-navy text-white/80">
      <div className="mx-auto max-w-6xl px-4 py-16 lg:px-8 lg:py-20">
        <div className="mb-12 flex flex-col items-start justify-between gap-4 border-b border-white/10 pb-10 sm:flex-row sm:items-center">
          <Logo chip height={44} />
          <div className="inline-flex items-center gap-2 rounded-md bg-white/10 px-3 py-2 text-xs font-semibold text-white/80">
            <ShieldCheck className="h-4 w-4 text-gold" /> Sicurezza Premium
          </div>
        </div>

        {partner ? (
          /* Affiliate: Titolare = affiliate, Responsabile art.28 = Montoro */
          <div className="grid gap-10 lg:grid-cols-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-gold">Titolare del trattamento</p>
              <p className="mt-2 font-heading text-base font-extrabold text-white">{partner.company_name}</p>
              <p className="text-sm text-white/60">{partnerLabel} ZERAMI</p>
              <ul className="mt-3 space-y-1.5 text-sm text-white/60">
                {partner.city ? (
                  <li className="flex items-start gap-2.5">
                    <MapPin className="mt-0.5 h-4 w-4 flex-none text-gold" /> {partner.city}
                  </li>
                ) : null}
                {partner.contact_email ? (
                  <li>
                    <a href={`mailto:${partner.contact_email}`} data-testid="footer-partner-email" className="flex items-center gap-2.5 transition-colors hover:text-gold">
                      <Mail className="h-4 w-4 text-gold" /> {partner.contact_email}
                    </a>
                  </li>
                ) : null}
                {partner.contact_phone ? (
                  <li>
                    <a href={`tel:${partner.contact_phone}`} className="flex items-center gap-2.5 transition-colors hover:text-gold">
                      <Phone className="h-4 w-4 text-gold" /> {partner.contact_phone}
                    </a>
                  </li>
                ) : null}
                {partner.referral_code ? <li className="text-white/40">Ref: {partner.referral_code}</li> : null}
              </ul>
              <div className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-brand-green/15 px-2.5 py-1 text-xs font-bold text-brand-green ring-1 ring-brand-green/30">
                <BadgeCheck className="h-3.5 w-3.5" /> {partnerLabel} ZERAMI
              </div>
            </div>
            <MontoroBlock heading="Responsabile del trattamento (Art. 28 GDPR)" />
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-gold">Legale</p>
              <ul className="mt-3 space-y-2 text-sm text-white/70">
                <li><a href="/privacy-policy" className="transition-colors hover:text-gold">Privacy Policy</a></li>
                <li><a href="/cookie-policy" className="transition-colors hover:text-gold">Cookie Policy</a></li>
                <li><a href="/eula" className="transition-colors hover:text-gold">Termini e Condizioni</a></li>
                <li><a href={COMPANY.codiceEticoUrl} target="_blank" rel="noreferrer" data-testid="footer-codice-etico-partner" className="transition-colors hover:text-gold">Codice Etico</a></li>
                <li><a href="#contatti" className="transition-colors hover:text-gold">Esercita i tuoi diritti GDPR</a></li>
              </ul>
            </div>
          </div>
        ) : (
          /* Generic ZERAMI: Titolare = Montoro */
          <div className="grid gap-10 lg:grid-cols-3">
            <MontoroBlock heading="Titolare del trattamento" />
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-gold">Contatti</p>
              <ul className="mt-3 space-y-3 text-sm">
                <li>
                  <a href={COMPANY.phoneHref} data-testid="footer-phone" className="flex items-center gap-2.5 transition-colors hover:text-gold">
                    <Phone className="h-4 w-4 text-gold" /> {COMPANY.phone}
                  </a>
                </li>
                <li>
                  <a href={COMPANY.emailHref} data-testid="footer-email" className="flex items-center gap-2.5 transition-colors hover:text-gold">
                    <Mail className="h-4 w-4 text-gold" /> {COMPANY.email}
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-gold">Legale</p>
              <ul className="mt-3 space-y-2 text-sm text-white/70">
                <li><a href="/privacy-policy" className="transition-colors hover:text-gold">Privacy Policy</a></li>
                <li><a href="/cookie-policy" className="transition-colors hover:text-gold">Cookie Policy</a></li>
                <li><a href="/eula" className="transition-colors hover:text-gold">Termini e Condizioni</a></li>
                <li><a href={COMPANY.codiceEticoUrl} target="_blank" rel="noreferrer" data-testid="footer-codice-etico" className="transition-colors hover:text-gold">Codice Etico</a></li>
                <li><a href="#contatti" className="transition-colors hover:text-gold">Esercita i tuoi diritti GDPR</a></li>
              </ul>
            </div>
          </div>
        )}

        <div className="mt-12 rounded-xl border border-white/10 bg-white/5 p-6">
          <p className="text-[11px] font-bold uppercase tracking-widest text-gold">
            Informazioni obbligatorie · AGCM / Codice del Consumo
          </p>
          <p className="mt-3 text-xs leading-relaxed text-white/55" data-testid="footer-disclaimer">
            {DISCLAIMER_AGCM}
          </p>
        </div>

        <div className="mt-8 flex flex-col items-start justify-between gap-2 border-t border-white/10 pt-6 text-xs text-white/50 sm:flex-row sm:items-center">
          <p>© {year} {titolar} — Tutti i diritti riservati.</p>
          <p>ZERAMI è un marchio di Alberto Montoro.</p>
        </div>
      </div>
    </footer>
  );
};
